import { getAuth } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Helper function to get Firebase auth token
const getAuthToken = async () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }
  return await auth.currentUser.getIdToken();
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint, options = {}) => {
  const token = await getAuthToken();
  const config = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers
    },
    ...options
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
  }

  return await response.json();
};

// RSVP Management Functions
export const rsvpApi = {
  // Submit or update RSVP
  submitRSVP: async (eventId, response, note = '') => {
    return await apiCall(`/api/events/response/${eventId}`, {
      method: 'POST',
      body: JSON.stringify({ response, note })
    });
  },

  // Get user's RSVP for a specific event
  getUserRSVP: async (eventId) => {
    return await apiCall(`/api/events/response/${eventId}/user`);
  },

  // Get all RSVPs for an event
  getEventRSVPs: async (eventId, includeUsers = true, responseFilter = null) => {
    const params = new URLSearchParams();
    if (includeUsers) params.append('includeUsers', 'true');
    if (responseFilter) params.append('response', responseFilter);
    
    const queryString = params.toString();
    const endpoint = `/api/events/response/${eventId}${queryString ? `?${queryString}` : ''}`;
    
    return await apiCall(endpoint);
  },

  // Remove RSVP
  removeRSVP: async (eventId) => {
    return await apiCall(`/api/events/response/${eventId}`, {
      method: 'DELETE'
    });
  },

  // Get user's RSVP history
  getUserRSVPHistory: async (options = {}) => {
    const {
      status = null,
      limit = 20,
      offset = 0,
      upcoming = false
    } = options;

    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (upcoming) params.append('upcoming', 'true');

    const queryString = params.toString();
    const endpoint = `/api/users/rsvps${queryString ? `?${queryString}` : ''}`;
    
    return await apiCall(endpoint);
  },

  // Get upcoming RSVPs
  getUpcomingRSVPs: async (response = null, limit = 10) => {
    const params = new URLSearchParams();
    if (response) params.append('response', response);
    if (limit) params.append('limit', limit.toString());

    const queryString = params.toString();
    const endpoint = `/api/users/rsvps/upcoming${queryString ? `?${queryString}` : ''}`;
    
    return await apiCall(endpoint);
  },

  // Get user RSVP statistics
  getUserRSVPStats: async () => {
    return await apiCall('/api/users/rsvps/stats');
  },

  // Get RSVP analytics for event (management only)
  getEventRSVPAnalytics: async (eventId) => {
    return await apiCall(`/api/events/${eventId}/rsvp-analytics`);
  }
};

// Notification Functions
export const notificationApi = {
  // Get user notifications
  getNotifications: async (options = {}) => {
    const {
      type = null,
      unread = false,
      limit = 50,
      offset = 0
    } = options;

    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (unread) params.append('unread', 'true');
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());

    const queryString = params.toString();
    const endpoint = `/api/notifications${queryString ? `?${queryString}` : ''}`;
    
    return await apiCall(endpoint);
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    return await apiCall(`/api/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
  },

  // Mark multiple notifications as read
  markMultipleAsRead: async (notificationIds) => {
    return await apiCall('/api/notifications/read-multiple', {
      method: 'PUT',
      body: JSON.stringify({ notificationIds })
    });
  },

  // Get notification statistics
  getNotificationStats: async () => {
    return await apiCall('/api/notifications/stats');
  }
};

// Utility functions for RSVP status checking
export const rsvpUtils = {
  // Check if user can RSVP based on deadline
  canRSVPByDeadline: (event) => {
    if (!event.rsvpDeadline) return true;
    return new Date() <= new Date(event.rsvpDeadline);
  },

  // Check if RSVP is allowed for event
  isRSVPAllowed: (event) => {
    return event.rsvpSettings?.allowRSVP !== false;
  },

  // Format RSVP response for display
  formatRSVPResponse: (response) => {
    const formatMap = {
      'Going': { text: 'Going', emoji: '✅', color: 'success' },
      'Not Going': { text: 'Not Going', emoji: '❌', color: 'danger' },
      'TBD': { text: 'Maybe', emoji: '❓', color: 'warning' }
    };
    return formatMap[response] || { text: response, emoji: '', color: 'secondary' };
  },

  // Calculate RSVP percentage
  calculateRSVPRate: (metrics) => {
    if (!metrics || !metrics.totalEligibleMembers || metrics.totalEligibleMembers === 0) {
      return 0;
    }
    return Math.round((metrics.rsvpTotal / metrics.totalEligibleMembers) * 100);
  },

  // Get RSVP status summary
  getRSVPSummary: (metrics) => {
    if (!metrics) return { total: 0, going: 0, notGoing: 0, tbd: 0 };
    
    return {
      total: metrics.rsvpTotal || 0,
      going: metrics.rsvpGoing || 0,
      notGoing: metrics.rsvpNotGoing || 0,
      tbd: metrics.rsvpTBD || 0,
      rate: metrics.rsvpRate || 0
    };
  }
};

// Error handling utilities
export const rsvpErrors = {
  // Parse API error messages
  parseError: (error) => {
    if (error.message.includes('Authentication required')) {
      return 'Please log in to RSVP to events.';
    }
    if (error.message.includes('Permission denied')) {
      return 'You do not have permission to RSVP to this event.';
    }
    if (error.message.includes('Deadline passed')) {
      return 'The RSVP deadline for this event has passed.';
    }
    if (error.message.includes('Event not found')) {
      return 'This event no longer exists.';
    }
    if (error.message.includes('Duplicate RSVP')) {
      return 'You have already RSVP\'d to this event.';
    }
    return error.message || 'An unexpected error occurred.';
  },

  // Check if error is recoverable
  isRecoverableError: (error) => {
    const nonRecoverableMessages = [
      'Event not found',
      'Deadline passed',
      'Permission denied'
    ];
    return !nonRecoverableMessages.some(msg => error.message.includes(msg));
  }
};

export default rsvpApi;
